import { Send } from 'lucide-react'
import { Badge, Button } from '@/components/common'
import { useConversationStore } from '@/stores/conversationStore'
import { cn } from '@/utils/cn'

/**
 * Sticky global Submit bar for the current round (Round model v2).
 *
 * Reads readiness for every active branch from the conversation store
 * and drives the global `submitRound` action. The button is disabled
 * until every active branch reports `ready`.
 */

export interface RoundSubmitBarProps {
  onSubmit: () => void | Promise<void>
  className?: string
}

export const RoundSubmitBar = ({ onSubmit, className }: RoundSubmitBarProps) => {
  const branches = useConversationStore((s) => s.branches)
  const currentRound = useConversationStore((s) => s.currentRound)
  const roundSubmitting = useConversationStore((s) => s.roundSubmitting)
  const getRoundReadiness = useConversationStore((s) => s.getRoundReadiness)
  const canSubmitRound = useConversationStore((s) => s.canSubmitRound)
  const newBranchesThisRound = useConversationStore((s) => s.newBranchesThisRound)

  const active = branches.filter((b) => b.status === 'active')
  const ready = active.filter((b) => {
    const r = getRoundReadiness(b._id)
    return r === 'ready' || r === 'no_questions'
  }).length
  const incomplete = active.length - ready

  const canSubmit = canSubmitRound()
  const nextRound = currentRound + 1

  return (
    <div
      className={cn(
        'sticky bottom-0 z-10 mt-4 mx-auto max-w-3xl',
        'rounded-2xl border border-slate-200 dark:border-slate-700',
        'bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-lg',
        'px-4 py-3 flex items-center gap-3',
        className,
      )}
    >
      <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
        <Badge variant="blue" size="sm">
          Round {currentRound}
        </Badge>
        <span className="text-slate-500 dark:text-slate-400">
          {ready}/{active.length} branches ready
        </span>
        {incomplete > 0 && (
          <Badge variant="yellow" size="sm">
            {incomplete} incomplete
          </Badge>
        )}
        {newBranchesThisRound > 0 && (
          <Badge variant="mint" size="sm">
            +{newBranchesThisRound} new
          </Badge>
        )}
      </div>

      <div className="ml-auto">
        <Button
          onClick={onSubmit}
          disabled={!canSubmit}
          isLoading={roundSubmitting}
          rightIcon={<Send className="w-4 h-4" />}
          title={
            canSubmit
              ? `Submit round ${currentRound} → ${nextRound}`
              : 'Answer or skip every question in every active branch first.'
          }
        >
          Submit Round {currentRound} → {nextRound}
        </Button>
      </div>
    </div>
  )
}

export default RoundSubmitBar
